import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { CompanyDepartmentService } from '../company-department.service';

const refreshDepartmentQueries = async (
	queryClient: ReturnType<typeof useQueryClient>,
	departmentId?: string,
) => {
	await queryClient.invalidateQueries({ queryKey: ['company-departments'] });
	if (departmentId) {
		await queryClient.invalidateQueries({
			queryKey: ['company-department', departmentId],
		});
	}
};

export const useCreateCompanyDepartment = (
	onSuccess?: (department: CompanyDepartments.CompanyDepartment) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: CompanyDepartments.CreateCompanyDepartment) =>
			CompanyDepartmentService.create(body),
		onSuccess: async (department) => {
			await refreshDepartmentQueries(queryClient, department.id);
			toast.success('Departamento criado com sucesso!');
			if (onSuccess) onSuccess(department);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar departamento'));
		},
	});
};

export const usePatchCompanyDepartment = (
	onSuccess?: (department: CompanyDepartments.CompanyDepartment) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: CompanyDepartments.UpdateCompanyDepartment;
		}) => CompanyDepartmentService.patch(id, body),
		onSuccess: async (department, variables) => {
			await refreshDepartmentQueries(queryClient, variables.id);
			toast.success('Departamento atualizado com sucesso!');
			if (onSuccess) onSuccess(department);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao atualizar departamento'),
			);
		},
	});
};

export const useRemoveCompanyDepartment = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => CompanyDepartmentService.remove(id),
		onSuccess: async (_department, id) => {
			await refreshDepartmentQueries(queryClient, id);
			toast.success('Departamento removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover departamento'));
		},
	});
};

export const useLinkCompanyDepartment = (
	onSuccess?: (link: CompanyDepartments.CompanyDepartmentLink) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			departmentId,
			body,
		}: {
			departmentId: string;
			body: CompanyDepartments.LinkCompanyDepartment;
		}) => CompanyDepartmentService.linkToCompany(departmentId, body),
		onSuccess: async (link, variables) => {
			await refreshDepartmentQueries(queryClient, variables.departmentId);
			toast.success('Departamento vinculado com sucesso!');
			if (onSuccess) onSuccess(link);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao vincular departamento'),
			);
		},
	});
};

export const useUnlinkCompanyDepartment = (
	onSuccess?: (link: CompanyDepartments.CompanyDepartmentLink) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			departmentId,
			companyId,
		}: {
			departmentId: string;
			companyId: string;
		}) =>
			CompanyDepartmentService.unlinkFromCompany(departmentId, companyId),
		onSuccess: async (link, variables) => {
			await refreshDepartmentQueries(queryClient, variables.departmentId);
			toast.success('Departamento desvinculado com sucesso!');
			if (onSuccess) onSuccess(link);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao desvincular departamento'),
			);
		},
	});
};

export const useCreateCompanySector = (
	onSuccess?: (sector: CompanyDepartments.CompanySector) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			departmentId,
			body,
		}: {
			departmentId: string;
			body: CompanyDepartments.CreateCompanySector;
		}) => CompanyDepartmentService.addSector(departmentId, body),
		onSuccess: async (sector, variables) => {
			await refreshDepartmentQueries(queryClient, variables.departmentId);
			toast.success('Setor criado com sucesso!');
			if (onSuccess) onSuccess(sector);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar setor'));
		},
	});
};

export const usePatchCompanySector = (
	onSuccess?: (sector: CompanyDepartments.CompanySector) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			departmentId,
			sectorId,
			body,
		}: {
			departmentId: string;
			sectorId: string;
			body: CompanyDepartments.UpdateCompanySector;
		}) =>
			CompanyDepartmentService.patchSector(departmentId, sectorId, body),
		onSuccess: async (sector, variables) => {
			await refreshDepartmentQueries(queryClient, variables.departmentId);
			toast.success('Setor atualizado com sucesso!');
			if (onSuccess) onSuccess(sector);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar setor'));
		},
	});
};

export const useRemoveCompanySector = (
	onSuccess?: (sector: CompanyDepartments.CompanySector) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			departmentId,
			sectorId,
		}: {
			departmentId: string;
			sectorId: string;
		}) => CompanyDepartmentService.removeSector(departmentId, sectorId),
		onSuccess: async (sector, variables) => {
			await refreshDepartmentQueries(queryClient, variables.departmentId);
			toast.success('Setor removido com sucesso!');
			if (onSuccess) onSuccess(sector);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover setor'));
		},
	});
};
